import { useFirestoreID, useFirestoreGeneralOrderBy, useFirestoreGeneralThreeOrderBy } from "../../../firebase/useFirestore"
import { useState, useEffect, useMemo } from "react";
import ScrollToTop from "../../../hooks/ScrollToTop";
import Location from "../../../hooks/Location"
import useResearchResponseActions from "../../../hooks/useResearchResponseActions";
import SelectableGrid from "../../common/SelectableGrid";
import ReadOnlyGuard from "../../common/ReadOnlyGuard";
import SuperAdmin from "../../../hooks/SuperAdmin";
import orderResponsesForDisplay, { isMatrixType } from "../../../helpers/questionnaire/orderResponsesForDisplay";
import ResearchResponseCards from "../../measurementPlan/ResearchResponseCards.jsx";
import ResearchResponsesPageHeader from "../../measurementPlan/ResearchResponsesPageHeader.jsx";

/**
 * De antwoorden van één deelnemer op één vraag, per meetmoment.
 *
 * Zelfde weergave en dezelfde rechten als "Responses" bij een onderzoek: alleen
 * super admins met bewerkrechten kunnen antwoorden toevoegen, wijzigen of
 * verwijderen. Het enige verschil is dat de antwoorden hier op één deelnemer
 * gefilterd zijn.
 */
const ResearchResponsesPersonas = () => {
    // State
    const [momentID, setMoment] = useState('')
    const [questionaireId, setQuestionaireId] = useState('')
    const [personaTitle, setPersonaTitle] = useState('')

    // Hooks
    ScrollToTop()
    const superAdmin = SuperAdmin().admin
    const personaId = Location()[3]
    const researchId = Location()[4]
    const fieldID = Location()[5]

    // Firestore
    // 'asc', niet 'desc': deze filtercombinatie heeft alleen een oplopende
    // samengestelde index. Een aflopende query faalt stil en het scherm toont
    // dan geen antwoorden terwijl ze er wel zijn. De weergavevolgorde regelt
    // orderResponsesForDisplay hieronder, voor beide schermen gelijk.
    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-missing-composite-index
    const results = useFirestoreGeneralThreeOrderBy(
        'QuestionnairesResponses',
        'FieldID', fieldID ? fieldID : '',
        'MomentID', momentID ? momentID : '',
        'Persona', personaId ? personaId : '',
        'Timestamp', 'asc')
    const momentsRaw = useFirestoreGeneralOrderBy('MeasureMoments', 'ResearchID', researchId ? researchId : '', 'Position', 'asc')
    const moments = useMemo(() => (momentsRaw || []).filter((m) => !m.Deleted), [momentsRaw])
    const fields = useFirestoreID('QuestionnaireFields', fieldID ? fieldID : '')
    const personas = useFirestoreID('Personas', personaId ? personaId : '')
    const matrixRows = useFirestoreGeneralOrderBy('MatrixQuestionRows', 'Field', fieldID ? fieldID : '', 'Position', 'asc')

    const field = Array.isArray(fields) && fields[0] ? fields[0] : null
    // Het vraagtype komt van het VELD, niet van het eerste antwoord: een
    // handmatig toegevoegd antwoord staat vooraan en draagt er geen (inbox #190).
    const orderedResults = orderResponsesForDisplay(results, matrixRows, field?.Type)
    // Bij een matrixvraag hoort een handmatig toegevoegd antwoord bij geen
    // enkele stelling — dan is de plusknop een knop die stille rommel maakt.
    const canAddResponse = !isMatrixType(field?.Type)

    // Set persona title in state
    useEffect(() => {
        personas && personas.forEach(persona => {
            setPersonaTitle(persona.Name)
        })
    }, [personas])

    // Set questionnaire id in state
    useEffect(() => {
        results && results.forEach(result => {
            setQuestionaireId(result.QuestionannaireID)
        })
    }, [results])

    // Set default moment in state
    useEffect(() => {
        setMoment(moments[0]?.ID)
    }, [moments])

    const { inputHandler, deleteResult, addResult } = useResearchResponseActions({
        fieldID,
        momentID,
        researchId,
        questionaireId,
        fieldType: field?.Type,
        personaId,
        results,
    })

    return (
        <div className="main">
            <div className="main-container">
                <ResearchResponsesPageHeader field={field} subHeading={personaTitle} />
                <SelectableGrid
                    items={moments || []}
                    getId={(item) => item.ID}
                    getLabel={(item) => item.Title}
                    selectedId={momentID}
                    onClick={setMoment}
                />
                <ReadOnlyGuard edit="measurement.edit">
                    {(canEdit) => (
                        // Toevoegen en wijzigen blijft bij Deccos; verwijderen (naar de
                        // prullenbak) mag de organisatie zelf, voor haar testinvullingen.
                        <ResearchResponseCards
                            results={orderedResults}
                            loaded={Array.isArray(results)}
                            canCrud={superAdmin && canEdit}
                            canDelete={canEdit}
                            canAdd={canAddResponse}
                            onInput={inputHandler}
                            onDelete={deleteResult}
                            onAdd={addResult}
                        />
                    )}
                </ReadOnlyGuard>
            </div>
        </div>
    )
}

export default ResearchResponsesPersonas
